import { useDragAndDrop } from "@formkit/drag-and-drop/react";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
  COLLECTION_TYPES,
  getPopupCollections,
  getPopups,
  updatePopupCollection,
} from "../../apis/PopupCollection";
import { showToast } from "../../utils/toast";

export const ReorderCollectionsDrawer = ({
  setEditOpen,
  reloadTable,
  setLoading,
}) => {
  // =========================
  // State
  // =========================

  const [popups, setPopups] = useState([]);
  const [popup, setPopup] = useState(null);
  const [collectionType, setCollectionType] = useState("edit");

  const [initialItems, setInitialItems] = useState([]);
  const [fetching, setFetching] = useState(false);

  const [parentRef, items, setItems] = useDragAndDrop([], {
    itemKey: (item) => item.id,
  });

  const changed = items.some(
    (item, index) => item.id !== initialItems[index]?.id,
  );

  // =========================
  // Effects
  // =========================

  useEffect(() => {
    getPopups()
      .then((response) => setPopups(response.results || []))
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    if (!popup) {
      setInitialItems([]);
      setItems([]);
      return;
    }

    let cancelled = false;

    const fetchCollections = async () => {
      setFetching(true);

      try {
        const response = await getPopupCollections(1000, 1, {
          popup: popup.id,
          collection_type: collectionType,
          ordering: "display_order",
        });

        if (cancelled) {
          return;
        }

        const results = response.results || [];
        setInitialItems(results);
        setItems(results);
      } catch (error) {
        console.error(error);
        showToast("Failed to load buckets", "error");
      } finally {
        if (!cancelled) {
          setFetching(false);
        }
      }
    };

    fetchCollections();

    return () => {
      cancelled = true;
    };
  }, [popup, collectionType, setItems]);

  // =========================
  // Handlers
  // =========================

  const handleSave = async () => {
    setLoading(true);

    try {
      // Only patch collections whose display_order actually changes
      const updates = items
        .map((item, index) => ({ ...item, display_order: index + 1 }))
        .filter(
          (item) =>
            item.display_order !==
            initialItems.find((initial) => initial.id === item.id)
              ?.display_order,
        );

      await Promise.all(
        updates.map((item) =>
          updatePopupCollection(item.id, { display_order: item.display_order }),
        ),
      );

      await reloadTable();

      showToast("Display order updated successfully", "success");

      setEditOpen(false);
    } catch (error) {
      console.error(error);

      showToast("Failed to update display order", "error");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Render
  // =========================

  return (
    <Box p={5}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Reorder Buckets</Typography>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            disabled={!changed}
            onClick={() => setItems(initialItems)}
          >
            Reset
          </Button>

          <Button variant="contained" disabled={!changed} onClick={handleSave}>
            Save Order
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <Autocomplete
          sx={{ flex: 1 }}
          options={popups}
          value={popup}
          getOptionLabel={(option) => option?.title || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setPopup(value)}
          renderInput={(params) => <TextField {...params} label="Popup" />}
        />

        <TextField
          select
          label="Type"
          value={collectionType}
          onChange={(e) => setCollectionType(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {COLLECTION_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Status messages */}
      {!popup && (
        <Typography color="text.secondary">
          Select a popup to reorder its buckets.
        </Typography>
      )}

      {fetching && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {popup && !fetching && !items.length && (
        <Typography color="text.secondary">
          No buckets of this type in the selected popup.
        </Typography>
      )}

      {/* Sortable list. Kept mounted so the drag-and-drop ref stays attached */}
      <Box
        ref={parentRef}
        display={fetching ? "none" : "flex"}
        flexDirection="column"
        gap={1}
      >
        {items.map((item, index) => (
          <ListItem
            key={item.id}
            sx={{
              cursor: "grab",
              bgcolor: "action.hover",
              borderRadius: 1,
              gap: 2,
            }}
          >
            <Chip size="small" label={index + 1} />

            <ListItemAvatar>
              <Avatar
                variant="square"
                src={item.image?.file}
                sx={{ width: 48, height: 48, borderRadius: 1 }}
              />
            </ListItemAvatar>

            <ListItemText
              primary={item.name || "Untitled"}
              secondary={`${item.products?.length ?? 0} products · ${
                item.active ? "Active" : "Inactive"
              }`}
            />

            <ListItemIcon sx={{ minWidth: 36 }}>
              <DragIndicatorIcon />
            </ListItemIcon>
          </ListItem>
        ))}
      </Box>
    </Box>
  );
};
